import './modal-task.scss';
import React, { useEffect, useMemo, useState } from 'react';
import Modal from '@mui/material/Modal';
import { Alert, Box, Snackbar, Switch } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Controller, useForm } from 'react-hook-form';
import Joi from 'joi';
import { joiResolver } from '@hookform/resolvers/joi';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimeField } from '@mui/x-date-pickers/TimeField';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

import apiClient from '../../../../utils/apiClient';
import { jwtDecode } from 'jwt-decode';
import { reassignCalendarEvent, saveCalendarEvent } from '../../../../api/calendarApi';
import {
  buildCalendarEventPayloads,
  getSelectedOwnerIds,
  isTimeInRange,
  normalizeToHHmm,
} from './modalTask.helpers';

const START_HOUR = 7;
const END_HOUR = 22;

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 760,
  boxSizing: 'border-box',
  textAlign: 'left',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
};

function ModalTask({ open, onClose, task, onSaved }) {
  const { t } = useTranslation();
  const token = sessionStorage.getItem('token') || null;
  const decodedToken = jwtDecode(token);
  const pmaId = decodedToken?.id;

  const RANGE_MSG = t('calendar-validation-time-range', {
    start: `${String(START_HOUR).padStart(2, '0')}:00`,
    end: `${String(END_HOUR).padStart(2, '0')}:00`,
  });

  const [owners, setOwners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [members, setMembers] = useState([]);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const isSystemTask = task?.created_by_id === 'SYSTEM';

  const schema = useMemo(
    () =>
      Joi.object({
        title: Joi.string().trim().required().messages({
          'string.empty': t('calendar-validation-title-required'),
          'any.required': t('calendar-validation-title-required'),
        }),

        owners: Joi.array().items(Joi.string()).min(1).max(task ? 1 : 50).required().messages({
          'any.required': t('calendar-validation-owner-required'),
          'array.min': t('calendar-validation-owner-required'),
          'array.max': t('calendar-validation-owner-required'),
        }),

        category: Joi.number().required().messages({
          'any.required': t('calendar-validation-category-required'),
          'number.base': t('calendar-validation-category-required'),
        }),

        tags: Joi.array().items(Joi.string()).default([]),

        startDate: Joi.any()
          .required()
          .custom((value, helpers) => {
            if (!value) return helpers.message(t('calendar-validation-start-date-required'));
            const d = dayjs(value);
            if (!d.isValid()) return helpers.message(t('calendar-validation-start-date-required'));
            return value;
          }),

        endDate: Joi.any()
          .allow(null)
          .custom((value, helpers) => {
            if (!value) return value;

            const end = dayjs(value);
            if (!end.isValid()) return helpers.message(t('calendar-validation-end-date-required'));

            const startValue = helpers?.state?.ancestors?.[0]?.startDate;
            if (startValue) {
              const start = dayjs(startValue);
              if (start.isValid() && end.isBefore(start, 'day')) {
                return helpers.message(t('calendar-validation-end-before-start'));
              }
            }
            return value;
          }),

        allDay: Joi.boolean().default(false),

        time: Joi.when('allDay', {
          is: false,
          then: Joi.string()
            .required()
            .custom((value, helpers) => {
              const normalizedValue = normalizeToHHmm(value);
              if (!normalizedValue) return helpers.message(t('calendar-validation-time-required'));
              if (!isTimeInRange(normalizedValue, START_HOUR, END_HOUR)) return helpers.message(RANGE_MSG);
              return normalizedValue;
            })
            .messages({
              'string.empty': t('calendar-validation-time-required'),
              'any.required': t('calendar-validation-time-required'),
            }),
          otherwise: Joi.any().allow(null),
        }),

        description: Joi.string().trim().allow('').max(2000).messages({
          'string.max': t('calendar-validation-description-too-long'),
        }),
      }),
    [RANGE_MSG, t, task]
  );

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    control,
    watch,
    formState: { errors },
    reset,
    setError,
    clearErrors,
  } = useForm({
    resolver: joiResolver(schema),
    mode: 'all',
    defaultValues: {
      title: '',
      owners: [],
      category: '',
      tags: [],
      startDate: null,
      endDate: null,
      allDay: false,
      time: '',
      description: '',
    },
  });

  const allDay = watch('allDay');

  const ownersOptions = useMemo(() => {
    const arr = Array.isArray(owners) ? [...owners] : [];
    return arr
      .map((o) => ({
        id: String(o.id),
        label: `${o?.name ?? ''} ${o?.last_name ?? ''}`.trim(),
        email: o?.email ?? '',
      }))
      .sort((a, b) => (a.label || '').toLowerCase().localeCompare((b.label || '').toLowerCase()));
  }, [owners]);

  const categoriesOptions = useMemo(
    () =>
      categories
        .filter((c) => Number(c.active) === 1)
        .map((c) => ({ value: c.id, label: c.category })),
    [categories]
  );

  const membersOptions = useMemo(() => {
    const arr = Array.isArray(members) ? [...members] : [];
    return arr
      .map((m) => ({
        id: String(m.id),
        label: `${m?.name ?? ''} ${m?.last_name ?? ''}`.trim(),
        email: m?.email ?? '',
      }))
      .sort((a, b) => (a.label || '').toLowerCase().localeCompare((b.label || '').toLowerCase()));
  }, [members]);

  useEffect(() => {
    if (!open) return;

    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    const fetchAll = async () => {
      try {
        const [catsRes, hacsRes, ownersRes] = await Promise.allSettled([
          apiClient.get(`${process.env.REACT_APP_API_URL}/calendar/categories`, config),
          apiClient.get(`${process.env.REACT_APP_API_URL}/pma/hacs?pmaId=${pmaId}`, config),
          apiClient.get(`${process.env.REACT_APP_API_URL}/pma/all`, config),
        ]);

        if (catsRes.status === 'fulfilled') setCategories(Array.isArray(catsRes.value.data) ? catsRes.value.data : []);
        if (hacsRes.status === 'fulfilled') setMembers(Array.isArray(hacsRes.value.data) ? hacsRes.value.data : []);
        if (ownersRes.status === 'fulfilled') setOwners(Array.isArray(ownersRes.value.data) ? ownersRes.value.data : []);
      } catch (e) {
        setSnackbarSeverity('error');
        setSnackbarMessage(t('calendar-reminder-error'));
        setSnackbarOpen(true);
      }
    };

    fetchAll();
  }, [open, token, pmaId]);

  useEffect(() => {
    if (!open) return;

    const toDayjsOrNull = (v) => (v ? dayjs(v) : null);

    if (task) {
      const ownerId = task.owner_id ?? task.owner ?? '';
      const assigned = Array.isArray(task.assigned_hacs) ? task.assigned_hacs : [];
      const assignedIds = assigned.map((h) => String(h?.id)).filter(Boolean);

      const initialAllDay = Boolean(task.all_day ?? task.allDay);
      const initialTime = normalizeToHHmm(task.starting_time) ?? '';

      reset({
        title: task.title ?? '',
        owners: ownerId ? [String(ownerId)] : [],
        category: task.category ?? task.category_id ?? '',
        tags: assignedIds,
        startDate: toDayjsOrNull(task.starting_date ?? task.startDate),
        endDate: toDayjsOrNull(task.ending_date ?? task.endDate),
        allDay: initialAllDay,
        time: initialAllDay ? '' : initialTime,
        description: task.description ?? '',
      });

      if (!initialAllDay && initialTime && !isTimeInRange(initialTime, START_HOUR, END_HOUR)) {
        setError('time', { type: 'manual', message: RANGE_MSG });
      } else {
        clearErrors('time');
      }
    } else {
      reset({
        title: '',
        owners: pmaId ? [String(pmaId)] : [],
        category: '',
        tags: [],
        startDate: null,
        endDate: null,
        allDay: false,
        time: '',
        description: '',
      });
      clearErrors('time');
    }
  }, [open, task, reset, pmaId, setError, clearErrors]);

  useEffect(() => {
    if (!open) return;
    if (task) return;

    const currentOwners = getValues('owners');
    if (Array.isArray(currentOwners) && currentOwners.length > 0) return;

    if (!pmaId) return;

    const exists = ownersOptions.some((o) => String(o.id) === String(pmaId));
    if (exists) {
      setValue('owners', [String(pmaId)], {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: true,
      });
    }
  }, [open, task, ownersOptions, pmaId, getValues, setValue]);

  useEffect(() => {
    if (allDay) {
      setValue('time', '', { shouldValidate: false });
      clearErrors('time');
    }
  }, [allDay, setValue, clearErrors]);

  const handleSubmitCapture = () => {
    const { startDate, endDate } = getValues();
    if (!endDate && startDate) {
      setValue('endDate', startDate, { shouldDirty: true, shouldTouch: true, shouldValidate: false });
    }
  };

  const onSubmit = async (data) => {
    const endDateFinal = data.endDate ?? data.startDate;

    const taskCategory = task?.category ?? task?.category_id ?? '';
    const taskAllDay = Boolean(task?.all_day ?? task?.allDay);
    const taskTime = normalizeToHHmm(task?.starting_time) ?? '';

    const taskAssigned = Array.isArray(task?.assigned_hacs) ? task.assigned_hacs : [];
    const taskAssignedIds = taskAssigned.map((h) => String(h?.id)).filter(Boolean);

    const finalTitle = isSystemTask ? (task?.title ?? data.title) : data.title;
    const finalCategory = Number(isSystemTask ? (taskCategory ?? data.category) : data.category);

    const finalStartDate = dayjs(data.startDate);
    const finalEndDate = dayjs(endDateFinal);

    const finalAllDay = isSystemTask ? taskAllDay : Boolean(data.allDay);

    const computedTime = isSystemTask
      ? (taskAllDay ? '' : (taskTime || normalizeToHHmm(data.time)))
      : (data.allDay ? '' : normalizeToHHmm(data.time));

    const finalTimeHHmm = finalAllDay ? '' : computedTime;

    if (!finalAllDay && finalTimeHHmm && !isTimeInRange(finalTimeHHmm, START_HOUR, END_HOUR)) {
      setError('time', { type: 'manual', message: RANGE_MSG });
      return;
    }

    const finalTags = isSystemTask ? taskAssignedIds : (Array.isArray(data.tags) ? data.tags : []);

    const basePayload = {
      id: task?.id ?? undefined,
      title: finalTitle,
      category: finalCategory,
      starting_date: finalStartDate.format('YYYY-MM-DD'),
      ending_date: finalEndDate.format('YYYY-MM-DD'),
      all_day: finalAllDay,
      starting_time: finalAllDay ? null : finalTimeHHmm,
      description: String(data.description ?? '').trim(),
      completed: false,
    };

    if (finalTags.length > 0) basePayload.assigned_hacs = finalTags;

    if (basePayload.id === undefined) delete basePayload.id;
    if (basePayload.starting_time === null) delete basePayload.starting_time;

    const selectedOwnerIds = getSelectedOwnerIds(data.owners);
    const payloads = buildCalendarEventPayloads(basePayload, selectedOwnerIds);
    const payload = payloads[0];

    const currentOwnerId = task?.owner_id ?? task?.owner;
    const isReassignment = Boolean(task)
      && String(currentOwnerId ?? '') !== payload.owner;

    try {
      const response = task
        ? (isReassignment
          ? await reassignCalendarEvent(token, payload, payload.owner)
          : await saveCalendarEvent(token, payload))
        : await saveCalendarEvent(token, payloads);

      if (response.status === 200 || response.status === 201) {
        setSnackbarMessage(t('common-saved'));
        setSnackbarSeverity('success');
        setSnackbarOpen(true);

        if (typeof onSaved === 'function') {
          onSaved({
            eventId: payload.id ?? null,
            ownerId: payload.owner,
            ownerIds: selectedOwnerIds,
            previousOwnerId: currentOwnerId ?? null,
            reassigned: isReassignment,
          });
        }

        setTimeout(() => {
          onClose();
        }, 300);
      }
    } catch (error) {
      setSnackbarSeverity('error');
      setSnackbarMessage(t('calendar-reminder-error'));
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  return (
    <>
      <Modal open={open} onClose={onClose} className="modal-task">
        <Box sx={style}>
          <button className="close" type="button" onClick={onClose}>
            <span className="icon-close" />
          </button>

          <h2>{task === null ? t('calendar-new-reminder') : t('calendar-edit-reminder')}</h2>

          <form onSubmitCapture={handleSubmitCapture} onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <div className="form-control required">
                  <label>{t('calendar-reminder-title')}</label>
                  <input type="text" {...register('title')} readOnly={isSystemTask} />
                  {errors.title && <p className="error-message">{errors.title.message}</p>}
                </div>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <div className="form-control required">
                  <label>{t('calendar-owner')}</label>
                  <Controller
                    name="owners"
                    control={control}
                    render={({ field }) => {
                      const selectedIds = getSelectedOwnerIds(field.value);
                      return (
                        <Autocomplete
                          multiple
                          disableCloseOnSelect={!task}
                          disablePortal
                          options={ownersOptions}
                          sx={{ width: '100%' }}
                          getOptionLabel={(option) => (typeof option === 'string' ? option : option?.label ?? '')}
                          isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                          value={ownersOptions.filter((option) => selectedIds.includes(String(option.id)))}
                          onBlur={field.onBlur}
                          onChange={(_, selected) => {
                            const nextOwners = task && selected.length > 1 ? selected.slice(-1) : selected;
                            field.onChange(nextOwners.map((option) => String(option.id)));
                          }}
                          renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                              {option.label}
                            </li>
                          )}
                          renderInput={(params) => <TextField {...params} placeholder={t('calendar-select-owner')} />}
                        />
                      );
                    }}
                  />

                  {errors.owners && <p className="error-message">{errors.owners.message}</p>}
                </div>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 5 }}>
                <div className="form-control required">
                  <label>{t('calendar-reminder-category')}</label>
                  <select
                    {...register('category', {
                      setValueAs: (v) => (v === '' ? '' : Number(v)),
                    })}
                    className="normal"
                    disabled={isSystemTask}
                  >
                    <option value="" disabled>
                      {t('calendar-select-category')}
                    </option>
                    {categoriesOptions.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  {errors.category && <p className="error-message">{errors.category.message}</p>}
                </div>
              </Grid>

              <Grid size={{ xs: 12, md: 7 }}>
                <div className="form-control">
                  <label>{t('calendar-tag-members')}</label>

                  <Controller
                    name="tags"
                    control={control}
                    defaultValue={[]}
                    render={({ field }) => (
                      <Autocomplete
                        multiple
                        className="normal"
                        options={membersOptions}
                        disabled={isSystemTask}
                        getOptionLabel={(option) => option?.label ?? ''}
                        isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                        value={membersOptions.filter((m) => (field.value || []).map(String).includes(String(m.id)))}
                        onChange={(_, selected) => field.onChange(selected.map((s) => String(s.id)))}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            {option.label}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} size="small" className="form-control" placeholder={t('calendar-select-members')} />
                        )}
                      />
                    )}
                  />
                </div>
              </Grid>
            </Grid>

              <LocalizationProvider dateAdapter={AdapterDayjs} dateFormats={{ keyboardDate: 'DD/MM/YYYY' }}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <div className="form-control required datepicker">
                      <label>{t('calendar-start-date')}</label>
                      <Controller
                        name="startDate"
                        control={control}
                        render={({ field }) => (
                          <DatePicker
                            format="DD/MM/YYYY"
                            value={field.value}
                            onChange={(nextValue) => {
                              field.onChange(nextValue);

                              const end = getValues('endDate');
                              const start = nextValue ? dayjs(nextValue) : null;
                              const endD = end ? dayjs(end) : null;

                              if (start && start.isValid() && (!endD || !endD.isValid() || start.isAfter(endD, 'day'))) {
                                setValue('endDate', nextValue, {
                                  shouldDirty: true,
                                  shouldTouch: true,
                                  shouldValidate: true,
                                });
                              }
                            }}
                            className="datepicker"
                            slotProps={{ textField: { size: 'small' } }}
                          />
                        )}
                      />
                      {errors.startDate && <p className="error-message">{errors.startDate.message}</p>}
                    </div>
                  </Grid>
                  {!isSystemTask ? (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <div className="form-control datepicker">
                      <label>{t('calendar-end-date')}</label>
                      <Controller
                        name="endDate"
                        control={control}
                        render={({ field }) => (
                          <DatePicker
                            format="DD/MM/YYYY"
                            value={field.value}
                            onChange={field.onChange}
                            disabled={isSystemTask}
                            className="datepicker"
                            slotProps={{ textField: { size: 'small' } }}
                          />
                        )}
                      />
                      {errors.endDate && <p className="error-message">{errors.endDate.message}</p>}
                    </div>
                  </Grid>
                  ) : null}
                </Grid>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <div className={`form-control datepicker ${allDay ? 'disabled' : 'required'}`}>
                      <label>{t('calendar-reminder-time')}</label>
                      <Controller
                        name="time"
                        control={control}
                        render={({ field }) => (
                          <TimeField
                            className="normal"
                            size="small"
                            format="HH:mm"
                            disabled={isSystemTask || allDay}
                            value={field.value ? dayjs(`2000-01-01T${field.value}`) : null}
                            onChange={(value) => {
                              const next = value && value.isValid ? (value.isValid() ? value.format('HH:mm') : '') : '';
                              field.onChange(next);

                              if (!next) {
                                if (errors?.time?.message === RANGE_MSG) clearErrors('time');
                                return;
                              }

                              if (!isTimeInRange(next, START_HOUR, END_HOUR)) {
                                setError('time', { type: 'manual', message: RANGE_MSG });
                              } else if (errors?.time?.message === RANGE_MSG) {
                                clearErrors('time');
                              }
                            }}
                            minTime={dayjs().hour(START_HOUR).minute(0).second(0)}
                            maxTime={dayjs().hour(END_HOUR).minute(0).second(0)}
                          />
                        )}
                      />
                      {!allDay && errors.time && <p className="error-message">{errors.time.message}</p>}
                    </div>
                  </Grid>

                  <Grid size={{ xs: 12, md: 8 }}>
                    <div className="form-control switch-row">
                      <label>{t('calendar-reminder-all-day')}</label>
                      <Controller
                        name="allDay"
                        control={control}
                        render={({ field }) => (
                          <Switch checked={field.value} disabled={isSystemTask} onChange={(e) => field.onChange(e.target.checked)} />
                        )}
                      />
                    </div>
                  </Grid>
                </Grid>
              </LocalizationProvider>


            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <div className="form-control">
                  <label>{t('calendar-reminder-description')}</label>
                  <textarea rows={4} {...register('description')} className="txt-area" />
                  {errors.description && <p className="error-message">{errors.description.message}</p>}
                </div>
              </Grid>
            </Grid>

            <div className="actions">
              <button className="btn secondary" type="button" onClick={onClose}>
                {t('common-cancel')}
              </button>
              <button className="btn" type="submit">
                {task === null ? t('calendar-create-reminder') : t('common-save')}
              </button>
            </div>
          </form>
        </Box>
      </Modal>

      <Snackbar open={snackbarOpen} autoHideDuration={2500} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default ModalTask;
